"use client"

import { useState, useEffect } from "react"
import axios from "axios"

const SalesManagement = () => {
  const [selectedItems, setSelectedItems] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [sales, setSales] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingSale, setEditingSale] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [saleToDelete, setSaleToDelete] = useState(null)
  const [isExporting, setIsExporting] = useState(false)
  const [fileInputRef, setFileInputRef] = useState(null)

  useEffect(() => {
    fetchSales()
  }, [])

  const fetchSales = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/get-sold-products`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )
      if (response.data && response.data.sales) {
        setSales(response.data.sales)
      } else {
        console.error("Unexpected API response format:", response.data)
        setError("Failed to fetch sales: Invalid response format")
      }
    } catch (err) {
      console.error("Error fetching sales:", err)
      setError(err.response?.data?.message || "Failed to fetch sales. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(sales.map((sale) => sale.id))
    } else {
      setSelectedItems([])
    }
  }

  const handleSelectItem = (id) => {
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
  }

  const filteredSales = sales.filter(
    (sale) =>
      sale.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.product_master_code.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const handleEditClick = (sale) => {
    setEditingSale(sale)
    setIsEditDialogOpen(true)
  }

  const handleEditSale = async (e) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)
      setError(null)
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URI}/api/edit-sales-product/${editingSale.id}`,
        {
          Date: editingSale.Date,
          product_master_code: editingSale.product_master_code,
          product_name: editingSale.product_name,
          category_name: editingSale.category_name,
          product_unit: editingSale.product_unit,
          product_color: editingSale.product_color,
          quantity_sold: Number(editingSale.quantity_sold),
          selling_price_per_unit: Number(editingSale.selling_price_per_unit)
        },
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data) {
        // Refresh the sales list after successful update
        await fetchSales()
        setIsEditDialogOpen(false)
        setEditingSale(null)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update sale")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteClick = (sale) => {
    setSaleToDelete(sale)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteSale = async () => {
    try {
      setIsSubmitting(true)
      setError(null)
      
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URI}/api/delete-sales-product/${saleToDelete.id}`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data) {
        // Refresh the sales list after successful deletion
        await fetchSales()
        setIsDeleteDialogOpen(false)
        setSaleToDelete(null)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete sale")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleExportClick = async () => {
    try {
      setIsExporting(true)
      setError(null)
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/export-sales-report`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          },
          responseType: 'blob' // Important for handling file download
        }
      )

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob)
      
      // Create a temporary link element
      const link = document.createElement('a')
      link.href = url
      link.download = `sales-report-${new Date().toISOString().split('T')[0]}.xlsx`
      
      // Append link to body, click it, and remove it
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up the URL
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err.response?.data?.message || "Failed to export sales report")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">Sales</h1>
            <p className="text-gray-500">Manage your sales</p>
          </div>
          <div className="flex gap-3">
            {/* <button 
              onClick={() => window.location.href = '/add-sale'}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Sale
            </button> */}
            <button 
              onClick={handleExportClick}
              disabled={isExporting}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {isExporting ? "Exporting..." : "Export Report"}
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by product name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">{error}</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 w-12">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === sales.length}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Product Code</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Product Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Color</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Sold On</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Product Unit</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Price Per Unit</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Total Price</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Quantity</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Category</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Added By</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Sales Return</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.map((sale) => (
                    <tr key={sale.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(sale.id)}
                          onChange={() => handleSelectItem(sale.id)}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">{sale.product_master_code}</td>
                      <td className="py-4 px-4 text-sm text-gray-900">{sale.product_name}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{sale.product_color}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{sale.Date ? formatDate(sale.Date) : formatDate(sale.created_at)}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{sale.product_unit}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">₹ {sale.selling_price_per_unit}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">₹ {sale.total_price}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{sale.quantity_sold}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{sale.category_name}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{sale.added_by}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{sale.sales_return}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => handleEditClick(sale)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button 
                            onClick={() => handleDeleteClick(sale)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Row Per Page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <svg
                  className="w-3 h-3 text-gray-400 -ml-6 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <span className="text-sm text-gray-600">Entries</span>
            </div>

            <div className="flex items-center gap-2">
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button className="w-8 h-8 rounded bg-orange-500 text-white text-sm font-medium">1</button>

              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Sale Dialog */}
      {isEditDialogOpen && editingSale && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative max-h-[90vh] flex flex-col">
            <button 
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 z-10"
              onClick={() => {
                setIsEditDialogOpen(false)
                setEditingSale(null)
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold mb-4">Edit Sale</h2>
            <div className="overflow-y-auto flex-1 pr-2">
              <form onSubmit={handleEditSale} className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
                <div className="space-y-1">
                  <label htmlFor="edit-code" className="block text-sm font-medium text-gray-700">Product Code</label>
                  <input
                    type="text"
                    id="edit-code"
                    value={editingSale.product_master_code}
                    onChange={(e) => setEditingSale(prev => ({ ...prev, product_master_code: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    placeholder="Enter product code"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">Product Name</label>
                  <input
                    type="text"
                    id="edit-name"
                    value={editingSale.product_name}
                    onChange={(e) => setEditingSale(prev => ({ ...prev, product_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    placeholder="Enter product name"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="edit-category" className="block text-sm font-medium text-gray-700">Category</label>
                  <input
                    type="text"
                    id="edit-category"
                    value={editingSale.category_name}
                    onChange={(e) => setEditingSale(prev => ({ ...prev, category_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    placeholder="Enter category name"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="edit-unit" className="block text-sm font-medium text-gray-700">Unit</label>
                  <input
                    type="text"
                    id="edit-unit"
                    value={editingSale.product_unit}
                    onChange={(e) => setEditingSale(prev => ({ ...prev, product_unit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    placeholder="Enter product unit"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="edit-color" className="block text-sm font-medium text-gray-700">Color</label>
                  <input
                    type="text"
                    id="edit-color"
                    value={editingSale.product_color}
                    onChange={(e) => setEditingSale(prev => ({ ...prev, product_color: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    placeholder="Enter product color"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="edit-quantity" className="block text-sm font-medium text-gray-700">Quantity</label>
                  <input
                    type="number"
                    id="edit-quantity"
                    value={editingSale.quantity_sold}
                    onChange={(e) => setEditingSale(prev => ({ ...prev, quantity_sold: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    placeholder="Enter quantity"
                    required
                    min="0"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="edit-price" className="block text-sm font-medium text-gray-700">Price Per Unit</label>
                  <input
                    type="number"
                    id="edit-price"
                    value={editingSale.selling_price_per_unit}
                    onChange={(e) => setEditingSale(prev => ({ ...prev, selling_price_per_unit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    placeholder="Enter price per unit"
                    required
                    min="0"
                    step="0.01"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="edit-sales-return" className="block text-sm font-medium text-gray-700">Sales Return</label>
                  <input
                    type="number"
                    id="edit-sales-return"
                    value={editingSale.sales_return}
                    onChange={(e) => setEditingSale(prev => ({ ...prev, sales_return: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    placeholder="Enter sales return"
                    required
                    min="0"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="edit-date" className="block text-sm font-medium text-gray-700">Sale Date</label>
                  <input
                    type="date"
                    id="edit-date"
                    value={editingSale.Date || new Date().toISOString().split('T')[0]}
                    onChange={(e) => setEditingSale(prev => ({ ...prev, Date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </form>
            </div>
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setIsEditDialogOpen(false)
                  setEditingSale(null)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleEditSale}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Update Sale"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Sale Dialog */}
      {isDeleteDialogOpen && saleToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <button 
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
              onClick={() => {
                setIsDeleteDialogOpen(false)
                setSaleToDelete(null)
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold mb-4">Delete Sale</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this sale? This action cannot be undone.
            </p>
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteDialogOpen(false)
                  setSaleToDelete(null)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteSale}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Deleting..." : "Delete Sale"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SalesManagement 
